/**
 * Style guide — the rules the outfit engine actually obeys.
 *
 * This tab exists because of rule 1: nothing configurable is hardcoded. These 28 rules drive
 * every suggestion and until now could only be changed with a SQL statement, which made them
 * hardcoded in every sense that matters.
 *
 * The design centres on ONE distinction — who said it. Seven rules came from Brian's own style
 * interview. Twenty-one were inferred, mostly from AI reference boards, and inference is how a
 * winter-only observation ("always add the third layer") got enforced in July for a month. So an
 * inferred rule is visually marked as a guess until a human reads it and says otherwise, and
 * confirming leaves a durable mark rather than being re-litigated every season.
 */
import { useState } from 'react';
import { api } from '../../lib/api';
import type { Season, StyleRule, StyleRuleKind } from '../../shared/types';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  Select,
  cx,
  errorText,
  useResource,
} from './ui';

const SEASONS: Season[] = ['spring', 'summer', 'autumn', 'winter'];

/** Order is deliberate: what to do, what never to do, then the descriptive detail. */
const KINDS: { id: StyleRuleKind; label: string; blurb: string }[] = [
  { id: 'prefer', label: 'Do this', blurb: 'Reach for these. The engine treats them as targets.' },
  { id: 'avoid', label: 'Never', blurb: 'Hard nos. The engine will not propose these at all.' },
  { id: 'palette', label: 'Palette', blurb: 'The colours everything else is built from.' },
  { id: 'silhouette', label: 'Silhouette', blurb: 'Fit and proportion.' },
  { id: 'note', label: 'Context', blurb: 'Background the engine reads but cannot act on directly.' },
];

const SOURCE_COPY: Record<StyleRule['source'], { chip: string; tone?: 'ok' | 'near'; help: string }> = {
  interview: { chip: 'Your words', tone: 'ok', help: 'Taken from your style interview.' },
  confirmed: { chip: 'Confirmed', tone: 'ok', help: 'Inferred, then read and confirmed by you.' },
  derived: {
    chip: 'Inferred — unconfirmed',
    tone: 'near',
    help: 'A guess, from reference images or your wardrobe. You have not confirmed it.',
  },
};

export default function StyleGuidePanel() {
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);

  const rules = useResource<StyleRule[]>(() => api.get<StyleRule[]>('/wardrobe/style-rules'), []);

  const all = rules.data ?? [];
  const unconfirmed = all.filter((r) => r.source === 'derived').length;
  const shown = onlyUnconfirmed ? all.filter((r) => r.source === 'derived') : all;

  async function confirm(rule: StyleRule) {
    setBusy(rule.id);
    setProblem(null);
    try {
      await api.put(`/wardrobe/style-rules/${rule.id}`, { confirm: true });
      rules.reload();
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function remove(rule: StyleRule) {
    setBusy(rule.id);
    try {
      await api.del(`/wardrobe/style-rules/${rule.id}`);
      rules.reload();
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="space-y-5">
      <PanelHeading
        title="Style guide"
        sub="The rules the outfit engine follows. Everything here is editable — if a rule is wrong, the outfits will be too."
      />

      {problem && (
        <Card tone="over">
          <p className="text-sm text-ink">{problem}</p>
        </Card>
      )}

      {rules.loading && <Loading label="Loading the style guide" rows={4} />}
      {rules.error && !rules.loading && (
        <ErrorState message={rules.error} onRetry={rules.reload} />
      )}

      {rules.data && !rules.loading && !rules.error && (
        <>
          {unconfirmed > 0 && (
            <Card tone="near">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="max-w-prose text-sm leading-relaxed text-ink">
                  <span className="font-semibold">
                    {unconfirmed} {unconfirmed === 1 ? 'rule was' : 'rules were'} inferred, not
                    stated by you.
                  </span>{' '}
                  Mostly read off the reference boards. One inferred rule &mdash; &ldquo;always add
                  the third layer&rdquo; &mdash; came from winter-only photos and was enforced all
                  summer before you caught it. Worth reading these and confirming or fixing each.
                </p>
                <Button onClick={() => setOnlyUnconfirmed((v) => !v)}>
                  {onlyUnconfirmed ? 'Show all' : 'Review these'}
                </Button>
              </div>
            </Card>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-ink-soft">
              {shown.length} of {all.length} {all.length === 1 ? 'rule' : 'rules'}
            </span>
            <Button variant="primary" onClick={() => setEditing('new')}>
              Add a rule
            </Button>
          </div>

          {editing === 'new' && (
            <RuleForm
              key="new"
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                rules.reload();
              }}
              onError={setProblem}
            />
          )}

          {shown.length === 0 && (
            <EmptyState title={onlyUnconfirmed ? 'Nothing left to review' : 'No rules yet'}>
              {onlyUnconfirmed
                ? 'Every inferred rule has been confirmed or corrected.'
                : 'Add the first one, or let the boards analysis derive some.'}
            </EmptyState>
          )}

          {KINDS.map(({ id, label, blurb }) => {
            const group = shown.filter((r) => r.kind === id);
            if (group.length === 0) return null;
            return (
              <div key={id}>
                <h3 className="font-display text-lg font-semibold text-ink">{label}</h3>
                <p className="mb-3 text-sm text-ink-soft">{blurb}</p>
                <ul className="space-y-3">
                  {group.map((r) =>
                    editing === r.id ? (
                      <li key={r.id}>
                        <RuleForm
                          key={`edit-${r.id}`}
                          rule={r}
                          onCancel={() => setEditing(null)}
                          onSaved={() => {
                            setEditing(null);
                            rules.reload();
                          }}
                          onError={setProblem}
                        />
                      </li>
                    ) : (
                      <li key={r.id}>
                        <RuleCard
                          rule={r}
                          busy={busy === r.id}
                          onEdit={() => setEditing(r.id)}
                          onConfirm={() => confirm(r)}
                          onRemove={() => remove(r)}
                        />
                      </li>
                    )
                  )}
                </ul>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

function RuleCard({
  rule,
  busy,
  onEdit,
  onConfirm,
  onRemove,
}: {
  rule: StyleRule;
  busy: boolean;
  onEdit: () => void;
  onConfirm: () => void;
  onRemove: () => void;
}) {
  const src = SOURCE_COPY[rule.source];
  return (
    <Card tone={rule.source === 'derived' ? 'near' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-display text-base font-semibold text-ink">{rule.label}</h4>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip tone={src.tone} title={src.help}>
              {src.chip}
            </Chip>
            {rule.seasons.length === 0 ? (
              <Chip>all year</Chip>
            ) : (
              rule.seasons.map((s) => <Chip key={s}>{s}</Chip>)
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {rule.source === 'derived' && (
            <Button variant="primary" onClick={onConfirm} disabled={busy}>
              {busy ? 'Saving…' : "That's right"}
            </Button>
          )}
          <Button onClick={onEdit} disabled={busy}>
            Edit
          </Button>
          <Button variant="danger" onClick={onRemove} disabled={busy}>
            Remove
          </Button>
        </div>
      </div>

      {rule.detail && (
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-ink-soft">{rule.detail}</p>
      )}
    </Card>
  );
}

function RuleForm({
  rule,
  onCancel,
  onSaved,
  onError,
}: {
  rule?: StyleRule;
  onCancel: () => void;
  onSaved: () => void;
  onError: (m: string | null) => void;
}) {
  const [label, setLabel] = useState(rule?.label ?? '');
  const [detail, setDetail] = useState(rule?.detail ?? '');
  const [kind, setKind] = useState<StyleRuleKind>(rule?.kind ?? 'prefer');
  const [seasons, setSeasons] = useState<Season[]>(rule?.seasons ?? []);
  const [saving, setSaving] = useState(false);

  function toggleSeason(s: Season) {
    setSeasons((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function save() {
    if (!label.trim()) return;
    setSaving(true);
    onError(null);
    try {
      const body = { label: label.trim(), detail: detail.trim() || null, kind, seasons };
      if (rule) await api.put(`/wardrobe/style-rules/${rule.id}`, body);
      else await api.post('/wardrobe/style-rules', body);
      onSaved();
    } catch (err) {
      onError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card tone="sunk">
      <div className="space-y-3">
        <label className="block text-sm text-ink-soft">
          <span className="mb-1 block">The rule, in a few words</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Brown leather boots as the default shoe"
            className="min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-sm text-ink focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
          />
        </label>

        <label className="block text-sm text-ink-soft">
          <span className="mb-1 block">Why, and any detail worth keeping</span>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            rows={4}
            placeholder="Where this came from, what it rules out, anything the engine should weigh."
            className="w-full rounded-lg border border-border bg-surface p-3 text-sm leading-relaxed text-ink focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
          />
        </label>

        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm text-ink-soft">
            <span className="mb-1 block">Kind</span>
            <Select
              value={kind}
              onChange={(e) => setKind(e.target.value as StyleRuleKind)}
              className="w-auto"
            >
              {KINDS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </Select>
          </label>

          <div className="text-sm text-ink-soft">
            {/* Seasons is the field that caused the layer-rule bug. Nothing selected means all
                year, and that is stated rather than left to be guessed. */}
            <span className="mb-1 block">
              Seasons <span className="text-xs">— none selected means all year</span>
            </span>
            <div className="flex flex-wrap gap-1.5">
              {SEASONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSeason(s)}
                  aria-pressed={seasons.includes(s)}
                  className={cx(
                    'min-h-11 rounded-lg border px-3 text-sm transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none',
                    seasons.includes(s)
                      ? 'border-sage bg-sage-soft text-ink'
                      : 'border-border bg-surface text-ink-soft hover:text-ink'
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="primary" onClick={save} disabled={saving || !label.trim()}>
            {saving ? 'Saving…' : rule ? 'Save changes' : 'Add rule'}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {rule?.source === 'derived' && (
            <span className="self-center text-xs text-ink-soft">
              Saving marks this as confirmed by you.
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
